import { Season, SkatingPass } from '@types';
import { Card, LoadingSpinner } from '_atoms';
import { SeasonDetailsService, SkatingPassService } from '_services';
import React, { useEffect, useState } from 'react';
import { MdInfo, MdLogin, MdPersonAdd } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';

interface SkatingPassPreviewProps {
  title?: string;
  message?: string;
  returnTo?: string;
}

const defaultSeasonSettings: Season = {
  id: '',
  startDate: '',
  endDate: '',
  isTicketPurchasesEnabled: false,
  isSeasonPassPurchasesEnabled: false,
  isPartyPackagePurchasesEnabled: false,
  isSkatingLessonPurchasesEnabled: false,
  isHockeyLessonPurchasesEnabled: false,
  sessionTimes: [],
};

export const SkatingPassPreview: React.FC<SkatingPassPreviewProps> = ({
  title = 'Sign In Required',
  message = 'Season passes are linked to your account so you can manage them anytime.',
  returnTo = '/passes',
}) => {
  const [isLoadingSeason, setIsLoadingSeason] = useState(true);
  const [isLoadingPasses, setIsLoadingPasses] = useState(true);
  const [seasonSettings, setSeasonSettings] = useState<Season>(
    defaultSeasonSettings,
  );
  const [passes, setPasses] = useState<SkatingPass[]>([]);
  const [error, setError] = useState<string | null>(null);

  const navigate = useNavigate();

  useEffect(() => {
    const loadSeason = async () => {
      setIsLoadingSeason(true);
      try {
        const response =
          (await SeasonDetailsService.getCurrentSeasonDetails()) as unknown as Season;
        if (response) {
          setSeasonSettings(response);
        }
      } catch (err) {
        console.error('Failed to load current season details:', err);
        setError('Unable to load season details right now.');
      } finally {
        setIsLoadingSeason(false);
      }
    };

    const loadPasses = async () => {
      setIsLoadingPasses(true);
      try {
        const response = await SkatingPassService.getSkatingPasses();
        if (Array.isArray(response)) {
          setPasses(response.filter(pass => pass.isActive));
        } else {
          setPasses([]);
        }
      } catch (err) {
        console.error('Failed to load skating passes:', err);
        setError('Unable to load skating passes right now.');
      } finally {
        setIsLoadingPasses(false);
      }
    };

    loadSeason();
    loadPasses();
  }, []);

  const handleAuthRedirect = (path: '/sign-in' | '/sign-up') => {
    navigate(path, { state: { returnTo } });
  };

  const renderPassCard = (pass: SkatingPass) => (
    <div
      key={pass.id}
      className="border border-gray-200 rounded-lg p-6 bg-white shadow-sm flex flex-col"
    >
      <img
        src={pass.photoUrl}
        alt={pass.name}
        className="w-full h-44 object-cover rounded-lg mb-6"
      />
      <div className="flex-1">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          {pass.name}
        </h3>
        <p
          className="text-gray-600 text-sm mb-4 prose prose-sm max-w-none [&_ul]:list-disc [&_ul]:pl-5 [&_li]:mb-1"
          dangerouslySetInnerHTML={{ __html: pass.description }}
        />
        <div className="space-y-2 text-sm">
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Max Uses:</span>
            <span className="font-semibold text-gray-900">
              {pass.maxUses === null || pass.maxUses === 0
                ? 'Unlimited'
                : `${pass.maxUses} sessions`}
            </span>
          </div>
        </div>
        {pass.disclaimer && (
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
            <p className="text-xs text-yellow-800">{pass.disclaimer}</p>
          </div>
        )}
      </div>

      <div className="mt-6 space-y-3">
        <div className="border border-gray-100 rounded-lg p-4">
          <div className="flex justify-between items-center text-sm text-gray-600">
            <span>Adult Pass</span>
            <span className="text-lg font-bold text-gray-900">
              ${pass.adultPrice}
            </span>
          </div>
          <button
            onClick={() => handleAuthRedirect('/sign-in')}
            className="mt-3 w-full flex items-center justify-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            <MdLogin className="w-4 h-4 mr-2" />
            Sign In to Purchase Adult Pass
          </button>
          <button
            onClick={() => handleAuthRedirect('/sign-up')}
            className="mt-2 w-full flex items-center justify-center px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors"
          >
            <MdPersonAdd className="w-4 h-4 mr-2" />
            Create Account to Purchase
          </button>
        </div>

        <div className="border border-gray-100 rounded-lg p-4">
          <div className="flex justify-between items-center text-sm text-gray-600">
            <span>Child Pass</span>
            <span className="text-lg font-bold text-gray-900">
              ${pass.childPrice}
            </span>
          </div>
          <button
            onClick={() => handleAuthRedirect('/sign-in')}
            className="mt-3 w-full flex items-center justify-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            <MdLogin className="w-4 h-4 mr-2" />
            Sign In to Purchase Child Pass
          </button>
          <button
            onClick={() => handleAuthRedirect('/sign-up')}
            className="mt-2 w-full flex items-center justify-center px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors"
          >
            <MdPersonAdd className="w-4 h-4 mr-2" />
            Create Account to Purchase
          </button>
        </div>
      </div>
    </div>
  );

  const renderPassPanel = () => {
    if (isLoadingPasses || isLoadingSeason) {
      return (
        <div className="flex justify-center items-center min-h-[300px]">
          <LoadingSpinner size="lg" />
        </div>
      );
    }

    if (!seasonSettings.isSeasonPassPurchasesEnabled) {
      return (
        <div className="flex items-center justify-center min-h-[300px]">
          <Card className="w-full max-w-md justify-center items-center border-none">
            <div className="text-center space-y-4">
              <MdInfo className="mx-auto text-gray-400 text-6xl" />
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Season Pass Purchases Not Available
                </h3>
                <p className="text-gray-600">
                  Season pass purchases are currently disabled. Please check
                  back later or contact us for more information.
                </p>
              </div>
            </div>
          </Card>
        </div>
      );
    }

    if (passes.length === 0) {
      return (
        <div className="text-center py-12">
          <MdInfo className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No passes available
          </h3>
          <p className="text-gray-500">
            There are currently no skating passes available for booking.
          </p>
        </div>
      );
    }

    return (
      <div className="grid gap-6 md:grid-cols-2">
        {passes.map(pass => renderPassCard(pass))}
      </div>
    );
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="bg-white rounded-lg shadow-lg p-4 md:p-6">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            View Available Season Passes
          </h2>
          <p className="text-gray-600">
            Review pass perks and pricing, then sign in or create an account to
            complete your purchase.
          </p>
          {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
        </div>
        {renderPassPanel()}
      </div>
    </div>
  );
};

export default SkatingPassPreview;
